import { useState, useEffect, useCallback } from 'react';
import client from '../api/client.js';

export function useLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await client.get('/lists');
      setLists(response.data);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to fetch lists';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createList = useCallback(async ({ title, description, is_personal = true }) => {
    const response = await client.post('/lists', { title, description, is_personal });
    setLists((prev) => [response.data, ...prev]);
    return response.data;
  }, []);

  const updateList = useCallback(async (id, updates) => {
    const response = await client.patch(`/lists/${id}`, updates);
    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, ...response.data } : l)));
    return response.data;
  }, []);

  const deleteList = useCallback(async (id) => {
    await client.delete(`/lists/${id}`);
    setLists((prev) => prev.filter((l) => l.id !== id));
  }, []);

  useEffect(() => {
    fetchLists().catch(() => {});
  }, [fetchLists]);

  return {
    lists,
    loading,
    error,
    fetchLists,
    createList,
    updateList,
    deleteList,
  };
}

export function useList(id) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await client.get(`/lists/${id}`);
      setList(response.data);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to fetch list';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchList().catch(() => {});
  }, [fetchList]);

  return { list, loading, error, refetch: fetchList };
}
